/**
 * Deposits: money taken before the work, and what it does to the bill after.
 *
 * ## The shape of the thing
 *
 * A deposit is not a third kind of money. It is an ordinary invoice, raised
 * early, for part of what an estimate came to. That choice is the reason this
 * file is small: payment links, Stripe, receipts, the paid/unpaid badge and
 * the receivables report already work on invoices, and none of them had to
 * learn a new word.
 *
 * What is left over is arithmetic, and it is all here so that the estimate
 * screen, the approval that raises the invoice, and the final bill cannot
 * disagree about a number the customer will check.
 *
 * ## The failure this is written against
 *
 * A deposit that is collected and then not deducted bills the customer twice.
 * $500 down on a $2,000 job, and a final invoice for $2,000, is a $2,500 job
 * and an angry phone call. The deduction is therefore not left to whoever
 * builds the invoice: `depositCreditLine` produces it, and the invoice
 * records what it credited (see `Invoice.depositCreditCents`) so that
 * re-issuing the same invoice cannot credit it a second time and turn a
 * double charge into a double refund.
 *
 * ## Why the credit is a line and not a payment
 *
 * The money arrived on the deposit invoice. Recording a payment against the
 * final invoice as well would mean the same cents are paid twice across the
 * books, and the day's takings would be wrong. A credit line says what is
 * true: this bill is for less, because some of it has already been settled
 * elsewhere, and here is the invoice number where that happened.
 *
 * The credit line is NOT taxable. Tax was computed on the full value of the
 * work, which is what is owed — a deposit changes when money moves, not how
 * much tax the job attracts. Making the credit taxable would quietly refund
 * part of the tax.
 */
import { applyBps, clampCents } from './money.js';
/**
 * What the deposit comes to, against a document total.
 *
 * Taken as a share of the TOTAL rather than the subtotal: "half down" is a
 * sentence about the number at the bottom of the quote, which is the number
 * the customer is looking at.
 *
 * Never negative, and never more than the total — a deposit larger than the
 * job is a typo, and billing it would leave the final invoice owing the
 * customer money.
 */
export function depositAmountCents(totalCents, rule) {
    const type = rule.depositType ?? 'none';
    if (type === 'none')
        return 0;
    if (totalCents <= 0)
        return 0;
    const raw = type === 'percent'
        ? applyBps(totalCents, clampDepositBps(rule.depositValueBps ?? 0))
        : (rule.depositValueCents ?? 0);
    return clampCents(raw, 0, totalCents);
}
/**
 * Percentages are clamped to 100%, unlike the discount path's generous
 * ceiling. A discount over 100% is caught by a later clamp against the
 * subtotal; a deposit over 100% has no honest meaning at all — nobody puts
 * down more than the price — so it is almost certainly bps confused with
 * percent, and silently charging 150% of a quote is the worst outcome
 * available.
 */
function clampDepositBps(bps) {
    const whole = Number.isInteger(bps) ? bps : Math.round(bps);
    return Math.min(Math.max(whole, 0), 10_000);
}
/** Does this estimate ask for anything up front? */
export function hasDeposit(rule) {
    const type = rule.depositType ?? 'none';
    if (type === 'none')
        return false;
    if (type === 'percent')
        return (rule.depositValueBps ?? 0) > 0;
    return (rule.depositValueCents ?? 0) > 0;
}
export const DEPOSIT_STATE_LABELS = {
    not_required: 'No deposit',
    unpaid: 'Deposit due',
    partial: 'Deposit part paid',
    paid: 'Deposit paid',
};
export function depositState(args) {
    if (!args.required || args.depositCents <= 0)
        return 'not_required';
    if (args.paidCents <= 0)
        return 'unpaid';
    if (args.paidCents < args.depositCents)
        return 'partial';
    return 'paid';
}
/**
 * Build the credit line, or null when there is nothing to credit.
 *
 * `paidCents` is what actually arrived on the deposit invoice, not what was
 * asked for. A deposit invoice for $500 that was only half paid credits $250,
 * because $250 is what the customer is out of pocket.
 */
export function depositCreditLine(args) {
    if (args.paidCents <= 0)
        return null;
    return {
        name: `Deposit received — invoice #${args.depositInvoiceNumber}`,
        kind: 'discount',
        quantity: 1,
        unitPriceCents: -args.paidCents,
        taxable: false,
    };
}
/** The single line on the deposit invoice itself. */
export function depositInvoiceLine(args) {
    return {
        name: `Deposit — estimate #${args.estimateNumber}`,
        kind: 'fee',
        quantity: 1,
        unitPriceCents: args.amountCents,
        /*
         * Not taxed here, because the work it belongs to is taxed in full on the
         * final invoice. Taxing the deposit as well would charge tax twice on the
         * same cents — once now and once on a total the credit line does not
         * reduce the tax of.
         */
        taxable: false,
    };
}
//# sourceMappingURL=deposits.js.map